/**
 * Field tickets (0461; api#923 tickets, #925 send + office-recorded approval,
 * #927 invoice gate) — the pieces the Tickets list, the ticket page and the
 * assembly's Tickets panel share.
 *
 * THE STATE MODEL IS THE API'S (lib/downhole-tickets.ts):
 *
 *   draft ──office-approve──▶ office_approved ──customer approves──▶ customer_approved
 *     ▲                          │    (review link, or the office          (final: notes only,
 *     │                          │     records a stamped copy)               cannot be deleted)
 *     └──── a substantive edit ──┤
 *                                └──customer rejects──▶ customer_rejected ──office-approve again──▶ …
 *
 * Office approval snapshots the prices; a draft carries none (`—`, never 0).
 * A run is billed only once its ticket is customer_approved.
 *
 * ROLES are the API's two lists: reads admin, office_staff, dispatcher (a
 * ticket carries prices); writes admin and office_staff. A button a role
 * cannot use is not rendered.
 *
 * 409s are branched on `code`, never on the sentence; the sentence is shown
 * as the API wrote it.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { useCreateTicket as useCreateTicketHook, useRecordTicketApproval, useSendTicket, useTicketRecipient, useUpdateTicket as useUpdateTicketHook } from '../../hooks/useDownhole';
import { CcField, RecipientPreview, parseCcInput } from './sendBits';
import { onlineOrSay } from '../../lib/downhole-offline';
import { dhSkin, type DhStatusSkin } from '../../lib/downhole-labels';
import type { DhRun, DhTicket, DhTicketEmailStatus, DhTicketStatus } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from './DownholeBits';
import { tenantLocalInput } from './RunDialogs';
import { formatInstantDay } from '../../lib/calendar-date';

export const DH_TICKET_READ_ROLES = ['admin', 'office_staff', 'dispatcher'] as const;
export const DH_TICKET_WRITE_ROLES = ['admin', 'office_staff'] as const;
export const canReadDownholeTickets = (role: string | null | undefined) =>
  !!role && (DH_TICKET_READ_ROLES as readonly string[]).includes(role);
export const canWriteDownholeTickets = (role: string | null | undefined) =>
  !!role && (DH_TICKET_WRITE_ROLES as readonly string[]).includes(role);

/**
 * dh_tickets.status. Four statuses, four meanings, four hues: amber is the
 * office's to do, blue is with the customer, green is signed, red is sent back.
 * Exhaustive; anything else is the slate `—`.
 */
export const DH_TICKET_STATUS_SKIN: Record<DhTicketStatus, DhStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  office_approved: { label: 'Awaiting customer', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  customer_approved: { label: 'Customer approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  customer_rejected: { label: 'Rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function TicketStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = dhSkin(DH_TICKET_STATUS_SKIN, status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** What the customer's email did, in words (dh_tickets.customer_email_status). Exhaustive. */
export const DH_TICKET_EMAIL_LABEL: Record<DhTicketEmailStatus, string> = {
  not_sent: 'Not sent',
  pending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  // Short: it is a band stat's value, over the hero circle's column.
  clicked: 'Link opened',
  failed: 'Failed',
  bounced: 'Bounced',
  complained: 'Spam report',
};
export const ticketEmailLabel = (s: string | null | undefined) =>
  s && Object.prototype.hasOwnProperty.call(DH_TICKET_EMAIL_LABEL, s) ? DH_TICKET_EMAIL_LABEL[s as DhTicketEmailStatus] : '—';

/** The 409 code on an API refusal, or null. Branch on this, never on the sentence. */
export function apiErrorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** RUNS_UNPRICED's flat `problems[]`, as the API sent it. */
export function apiErrorProblems(error: unknown): string[] {
  const p = (error as { response?: { data?: { problems?: unknown } } })?.response?.data?.problems;
  return Array.isArray(p) ? p.map(String) : [];
}

/**
 * A run that can go on a NEW ticket: closed or mobilized-not-run, on no live
 * ticket, not on a live invoice (billed before tickets existed — it needs
 * none). Unknown billing or ticket state is not ticketable: absent is "not
 * known", never "free".
 */
export const isTicketable = (r: DhRun) =>
  (r.run_state === 'closed' || r.run_state === 'not_run') && r.invoiced === false && r.dh_ticket_id === null;

/**
 * Why a set of runs cannot share a ticket, before the API is asked — its
 * rules (ticketHeaderFromRuns): every run names a customer, one customer, one
 * province or state. The API still refuses with the same codes if this is
 * wrong; this only saves the round trip.
 */
export function ticketRunsBlocker(runs: DhRun[]): string | null {
  if (runs.length === 0) return 'Pick at least one run.';
  const missing = runs.filter((r) => !r.customer_id);
  if (missing.length) {
    return `Run ${missing.map((r) => r.run_number ?? '—').join(', ')} ${missing.length === 1 ? 'has' : 'have'} no customer. Set one on the assembly or the run first.`;
  }
  if (new Set(runs.map((r) => r.customer_id)).size > 1) return 'These runs belong to different customers. One ticket goes to one customer.';
  const provinces = new Set(runs.map((r) => r.province_state ?? 'not set'));
  if (provinces.size > 1) return `These runs were worked in different provinces or states (${Array.from(provinces).join(', ')}). One ticket covers one.`;
  return null;
}

/** The note shown where a refusal happened — the API's own sentence, and its problems list if any. */
export function RefusalNote({ refusal }: { refusal: { message: string; problems: string[] } | null }) {
  if (!refusal) return null;
  return (
    <div role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
      <p className="font-bold">{refusal.message}</p>
      {refusal.problems.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {refusal.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Send (or resend) the review link. The API emails the customer's main
 * contact, else its accounting email, unless an address is typed here; it
 * answers with `sent_to`, which is what the office is shown. A resend reuses
 * the same link with a fresh seven-day expiry. A failed email is a 502 with
 * EMAIL_SEND_FAILED: nothing is stamped as sent, and its sentence is shown.
 */
export function SendTicketDialog({
  open,
  onClose,
  ticket,
  tz,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  ticket: DhTicket;
  tz: string | null;
  onSent: (sentTo: string, sentCc: string[]) => void;
}) {
  const send = useSendTicket();
  const opened = useOpenCount(open);
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [ccText, setCcText] = useState('');
  // The preview follows what is typed, once it settles: an entered address is
  // the recipient (source "entered"), else the customer's main contact, else
  // its accounting email — the API's order, from the API.
  const [typed, setTyped] = useState<{ email?: string; contact_name?: string }>({});
  React.useEffect(() => {
    const t = setTimeout(() => {
      const e = email.trim();
      const c = contact.trim();
      setTyped({ ...(e && EMAIL_RE.test(e) ? { email: e } : {}), ...(c ? { contact_name: c } : {}) });
    }, 400);
    return () => clearTimeout(t);
  }, [email, contact]);
  const preview = useTicketRecipient(ticket.id, typed, open);
  const noAddress = !!preview.data?.data && !preview.data.data.to;
  const cc = parseCcInput(ccText);
  const [refusal, setRefusal] = useState<{ message: string; problems: string[] } | null>(null);
  // EMAIL_SEND_FAILED: the link was made and works; only the email did not go.
  // The API hands it back so the office can send it some other way.
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  React.useEffect(() => {
    if (opened === 0) return;
    setEmail('');
    setContact('');
    setCcText('');
    setRefusal(null);
    setReviewUrl(null);
  }, [opened]);
  const resend = !!ticket.sent_to_customer_at;

  const submit = async () => {
    const to = email.trim();
    if (to && !EMAIL_RE.test(to)) {
      setRefusal({ message: 'Enter a valid email address, or leave it blank to use the one on file.', problems: [] });
      return;
    }
    if (cc.problem) return;
    if (!onlineOrSay(toast.error)) return;
    setRefusal(null);
    try {
      const res = await send.mutateAsync({
        id: ticket.id,
        ...(to && { email: to }),
        ...(contact.trim() && { contact_name: contact.trim() }),
        ...(cc.cc.length ? { cc: cc.cc } : {}),
      });
      const raw = res.raw as { sent_to?: unknown; sent_cc?: unknown } | null;
      const sentTo = String(raw?.sent_to ?? to ?? '');
      const sentCc = Array.isArray(raw?.sent_cc) ? raw!.sent_cc.map(String) : [];
      toast.success(`${ticket.ticket_number} sent to ${sentTo || 'the customer'}`);
      onSent(sentTo, sentCc);
      onClose();
    } catch (error) {
      // NO_CUSTOMER_EMAIL asks for an address; EMAIL_SEND_FAILED says the
      // email did not go. Both are shown as the API worded them.
      setRefusal({ message: getApiErrorMessage(error, 'The ticket was not sent.'), problems: [] });
      const url = (error as { response?: { data?: { data?: { review_url?: unknown } } } })?.response?.data?.data?.review_url;
      setReviewUrl(apiErrorCode(error) === 'EMAIL_SEND_FAILED' && typeof url === 'string' ? url : null);
    }
  };

  return (
    <DhDialog
      open={open}
      title={`${resend ? 'Resend' : 'Send'} ${ticket.ticket_number} to the customer`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={send.isPending || !!cc.problem || (noAddress && !email.trim())} onClick={submit}>
            {send.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {resend ? 'Resend' : 'Send'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fieldiq-text">
          {ticket.customer_name || 'The customer'} gets an email with a link to review the ticket and approve or reject it.
          {resend ? ' The link is the same one; it is good for another seven days.' : ' The link is good for seven days.'}
        </p>
        <RefusalNote refusal={refusal} />
        {reviewUrl && (
          <div className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
            <p>The review link was made and works. You can send it to the customer yourself:</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 basis-56 break-all text-xs">{reviewUrl}</code>
              <button
                type="button"
                className={DH_SECONDARY}
                onClick={() => {
                  navigator.clipboard?.writeText(reviewUrl).then(
                    () => toast.success('Link copied'),
                    () => toast.error('The link could not be copied; select it instead.'),
                  );
                }}
              >
                Copy link
              </button>
            </div>
          </div>
        )}
        <RecipientPreview query={preview} tz={tz} subject="who this ticket goes to" />
        <DhField label="Send to" hint="Leave blank to use the customer's main contact, or their accounting email if there is none.">
          <input className={DH_INPUT} type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
        </DhField>
        <DhField label="Contact name" hint="Optional. Blank uses the customer's main contact.">
          <input className={DH_INPUT} value={contact} onChange={(e) => setContact(e.target.value)} maxLength={255} />
        </DhField>
        <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
      </div>
    </DhDialog>
  );
}

const STAMP_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const STAMP_MAX = 20 * 1024 * 1024;

/**
 * The office records the customer's approval from the stamped copy the
 * company man sent back (decided 2026-10-06): the file, who approved it, and
 * the day — a tenant day, not in the future and not before the office
 * approved the ticket (the API checks both; the picker caps at today).
 */
export function RecordApprovalDialog({
  open,
  onClose,
  ticket,
  tz,
}: {
  open: boolean;
  onClose: () => void;
  ticket: DhTicket;
  tz: string | null;
}) {
  const record = useRecordTicketApproval();
  const opened = useOpenCount(open);
  const today = tenantLocalInput(new Date(), tz).slice(0, 10);
  const officeDay = tenantLocalInput(ticket.office_approved_at, tz).slice(0, 10);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [day, setDay] = useState('');
  const [refusal, setRefusal] = useState<{ message: string; problems: string[] } | null>(null);
  React.useEffect(() => {
    if (opened === 0) return;
    setFile(null);
    setName('');
    setDay(tenantLocalInput(new Date(), tz).slice(0, 10));
    setRefusal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const submit = async () => {
    const problems: string[] = [];
    if (!file) problems.push('Attach the stamped ticket the customer sent back.');
    else if (!STAMP_TYPES.includes(file.type)) problems.push('Attach a PDF, PNG or JPEG file.');
    else if (file.size > STAMP_MAX) problems.push('The file is larger than 20 MB.');
    if (!name.trim()) problems.push('Enter the name of the person who approved it.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) problems.push('Enter the day it was approved.');
    else if (today && day > today) problems.push("The approval day can't be in the future.");
    else if (officeDay && day < officeDay) problems.push("The approval day can't be before the office approved the ticket.");
    if (problems.length) {
      setRefusal({ message: problems.length === 1 ? problems[0] : 'Some details are missing.', problems: problems.length === 1 ? [] : problems });
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    setRefusal(null);
    const form = new FormData();
    form.append('file', file as File);
    form.append('approved_by_name', name.trim());
    form.append('approved_on', day);
    try {
      await record.mutateAsync({ id: ticket.id, form });
      toast.success(`${ticket.ticket_number} recorded as approved by ${name.trim()}`);
      onClose();
    } catch (error) {
      setRefusal({ message: getApiErrorMessage(error, 'The approval was not recorded.'), problems: [] });
    }
  };

  return (
    <DhDialog
      open={open}
      title={`Record the customer's approval · ${ticket.ticket_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={record.isPending} onClick={submit}>
            {record.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record approval
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fieldiq-text">
          For a ticket the company man stamped and sent back. The file is kept with the ticket, and the ticket&rsquo;s runs can then be
          invoiced. This cannot be undone.
        </p>
        <RefusalNote refusal={refusal} />
        <DhField label="Stamped ticket *" hint="PDF, PNG or JPEG, up to 20 MB.">
          <input
            className={`${DH_INPUT} py-1.5`}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </DhField>
        <div className="grid gap-4 sm:grid-cols-2">
          <DhField label="Approved by *">
            <input className={DH_INPUT} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </DhField>
          <DhField label="Approved on *" hint={tz ? `A day on the ${tz.replace(/_/g, ' ')} calendar` : undefined}>
            <input
              className={DH_DATE_INPUT}
              type="date"
              value={day}
              min={officeDay || undefined}
              max={today || undefined}
              onChange={(e) => setDay(e.target.value)}
            />
          </DhField>
        </div>
      </div>
    </DhDialog>
  );
}

const TEXT_FIELDS: { key: 'lsd' | 'afe_number' | 'po_number'; label: string; max: number }[] = [
  { key: 'lsd', label: 'LSD / location', max: 100 },
  { key: 'afe_number', label: 'AFE number', max: 100 },
  { key: 'po_number', label: 'PO number', max: 100 },
];

type TicketTexts = { lsd: string; afe_number: string; po_number: string; customer_notes: string; notes: string };
const textsOf = (t: DhTicket | null): TicketTexts => ({
  lsd: t?.lsd ?? '',
  afe_number: t?.afe_number ?? '',
  po_number: t?.po_number ?? '',
  customer_notes: t?.customer_notes ?? '',
  notes: t?.notes ?? '',
});

/**
 * New ticket (from an assembly's closed runs) or Edit (with `ticket`).
 *
 * An edit sends only what changed: `null` clears a field, an unchanged one is
 * not sent. Anything but the internal notes un-signs an office-approved or
 * rejected ticket back to draft — the dialog says so before Save, and the API
 * answers `reverted_to_draft`. A customer-approved ticket takes its internal
 * notes only (409 TICKET_CUSTOMER_APPROVED otherwise), so nothing else is
 * offered.
 */
export function TicketFormDialog({
  open,
  onClose,
  assemblyId,
  assemblyNumber,
  runs,
  runsLoaded,
  ticket = null,
  tz,
  onSaved,
  daily = false,
}: {
  open: boolean;
  onClose: () => void;
  /** null for a daily ticket, which is edited only (it is opened from the well job's day). */
  assemblyId: string | null;
  assemblyNumber: string | null;
  /** Every run of the assembly, as the API read them. */
  runs: DhRun[];
  runsLoaded: boolean;
  ticket?: DhTicket | null;
  tz: string | null;
  onSaved?: (id: string) => void;
  /**
   * A daily ticket (api#1062): its runs are the day's, set by the API, and a
   * PATCH with run_ids is refused (409 TICKET_IS_DAILY). So no run picker, no
   * run_ids, and no wait for an assembly's runs; the text fields stay.
   */
  daily?: boolean;
}) {
  const create = useCreateTicketHook();
  const update = useUpdateTicketHook();
  const editing = !!ticket;
  const notesOnly = ticket?.status === 'customer_approved';
  const signed = ticket?.status === 'office_approved' || ticket?.status === 'customer_rejected';
  const opened = useOpenCount(open);
  const [texts, setTexts] = useState<TicketTexts>(textsOf(ticket));
  const [baseline, setBaseline] = useState<TicketTexts>(textsOf(ticket));
  const [picked, setPicked] = useState<string[]>([]);
  const [baseRuns, setBaseRuns] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<{ message: string; problems: string[] } | null>(null);
  const [seeded, setSeeded] = useState(0);

  // Seeded once per opening, and only once the runs are known (useOpenCount):
  // a runs refetch while the dialog is open never resets what was picked.
  React.useEffect(() => {
    if (!open || opened === 0 || seeded === opened || (!runsLoaded && !daily)) return;
    const start = textsOf(ticket);
    const onTicket = ticket ? runs.filter((r) => r.dh_ticket_id === ticket.id).map((r) => r.id) : [];
    setTexts(start);
    setBaseline(start);
    setPicked(onTicket);
    setBaseRuns(onTicket);
    setRefusal(null);
    setSeeded(opened);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opened, seeded, runsLoaded]);

  const choosable = runs
    .filter((r) => isTicketable(r) || (ticket && r.dh_ticket_id === ticket.id))
    .sort((a, b) => (a.run_number ?? 0) - (b.run_number ?? 0));
  const elsewhere = runs.filter((r) => r.dh_ticket_id && r.dh_ticket_id !== ticket?.id);
  const selected = choosable.filter((r) => picked.includes(r.id));
  const runsChanged = picked.length !== baseRuns.length || picked.some((id) => !baseRuns.includes(id));
  const changedTexts = (Object.keys(texts) as (keyof TicketTexts)[]).filter((k) => texts[k].trim() !== baseline[k].trim());
  const substantive = runsChanged || changedTexts.some((k) => k !== 'notes');
  const blocker = notesOnly || daily ? null : ticketRunsBlocker(selected);

  const submit = async () => {
    if (blocker) return;
    if (!onlineOrSay(toast.error)) return;
    setRefusal(null);
    const body: Record<string, unknown> = {};
    for (const k of changedTexts) {
      if (notesOnly && k !== 'notes') continue;
      body[k] = texts[k].trim() || null;
    }
    try {
      if (editing && ticket) {
        // A daily ticket never sends run_ids (409 TICKET_IS_DAILY): its picker is not drawn and no
        // assembly runs load, so `picked` cannot move. One gate, the form; not a second check here.
        if (!notesOnly && runsChanged) body.run_ids = picked;
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        const res = await update.mutateAsync({ id: ticket.id, body });
        const reverted = (res.raw as { reverted_to_draft?: unknown } | null)?.reverted_to_draft === true;
        toast.success(reverted ? `${ticket.ticket_number} saved — back to draft for the office to approve again` : `${ticket.ticket_number} saved`);
        onClose();
        onSaved?.(ticket.id);
      } else {
        // A new ticket: blanks are simply not sent.
        for (const k of Object.keys(body)) if (body[k] === null) delete body[k];
        // Only a per-assembly ticket is created here; a daily ticket is opened from its well job's day.
        const res = await create.mutateAsync({ assembly_id: assemblyId ?? undefined, run_ids: picked, ...body });
        toast.success(`${res.data?.ticket_number ?? 'Ticket'} created as a draft`);
        onClose();
        if (res.data?.id) onSaved?.(res.data.id);
      }
    } catch (error) {
      setRefusal({ message: getApiErrorMessage(error, editing ? 'The ticket was not saved.' : 'The ticket was not created.'), problems: apiErrorProblems(error) });
    }
  };

  const busy = create.isPending || update.isPending;
  const set = (k: keyof TicketTexts) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTexts((t) => ({ ...t, [k]: e.target.value }));

  return (
    <DhDialog
      open={open}
      wide
      title={editing ? `Edit ${ticket?.ticket_number}` : `New field ticket · ${assemblyNumber ?? ''}`.trim()}
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto min-w-0 basis-full self-center text-xs text-fieldiq-muted sm:basis-auto sm:flex-1">
            {blocker ??
              (editing
                ? signed && substantive
                  ? 'Saving sends the ticket back to draft. The office approves it again before it goes to the customer.'
                  : ''
                : `${selected.length} ${selected.length === 1 ? 'run' : 'runs'} — a draft ticket is created; nothing is priced or sent yet.`)}
          </span>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={!!blocker || busy || (editing && !runsLoaded && !daily)} onClick={submit}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save ticket' : 'Create draft ticket'}
          </button>
        </>
      }
    >
      {(!runsLoaded && !daily) || (seeded !== opened && open) ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading runs" />
        </div>
      ) : (
        <div className="space-y-5">
          <RefusalNote refusal={refusal} />
          {notesOnly && (
            <p role="note" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
              The customer has approved this ticket, so only its internal notes can change.
            </p>
          )}
          {!notesOnly && !daily && (
            <fieldset>
              <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Runs on the ticket</legend>
              {choosable.length === 0 ? (
                <p className="text-sm text-fieldiq-text">
                  No run of this assembly can go on a ticket. A run can once it is closed (or recorded as mobilized, not run), is not on
                  another ticket and is not already invoiced.
                </p>
              ) : (
                <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
                  {choosable.map((r) => (
                    <li key={r.id} className="px-3 py-3 text-sm">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={picked.includes(r.id)}
                          onChange={() => setPicked((all) => (all.includes(r.id) ? all.filter((x) => x !== r.id) : [...all, r.id]))}
                          aria-label={`Ticket run ${r.run_number ?? '—'}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-mono font-extrabold text-fieldiq-accent-signal">Run {r.run_number ?? '—'}</span>
                          <span className="ml-2 text-xs text-fieldiq-muted">
                            {r.run_state === 'not_run' ? 'Mobilized, not run' : `In ${formatInstantDay(r.run_in_at, tz) ?? '—'}`} ·{' '}
                            {r.customer_name || 'no customer'} · {r.province_state ?? 'province/state not set'}
                          </span>
                          <span className="block text-xs text-fieldiq-text">{[r.well_name, r.rig_name].filter(Boolean).join(' · ') || '—'}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {elsewhere.length > 0 && (
                <p className="mt-2 text-xs text-fieldiq-muted">
                  Already on another ticket: {elsewhere.map((r) => `run ${r.run_number ?? '—'} (${r.ticket_number ?? '—'})`).join(', ')}.
                </p>
              )}
            </fieldset>
          )}
          {!notesOnly && (
            <div className="grid gap-4 sm:grid-cols-3">
              {TEXT_FIELDS.map((f) => (
                <DhField key={f.key} label={f.label}>
                  <input className={DH_INPUT} value={texts[f.key]} onChange={set(f.key)} maxLength={f.max} />
                </DhField>
              ))}
            </div>
          )}
          {!notesOnly && (
            <DhField label="Notes for the customer" hint="Printed on the ticket the customer reviews.">
              <textarea className={`${DH_INPUT} h-20 py-2`} value={texts.customer_notes} onChange={set('customer_notes')} maxLength={4000} />
            </DhField>
          )}
          <DhField label="Internal notes" hint="The office's own. Never shown to the customer; changing them does not un-sign the ticket.">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={texts.notes} onChange={set('notes')} maxLength={4000} />
          </DhField>
        </div>
      )}
    </DhDialog>
  );
}
